import type { LLMMessage, LLMStreamHandlers } from '@/IsekaiRPG/modules/llm/llmTypes';
import { LogService } from '@/IsekaiRPG/shared/logging/logService';
import { LLMEmptyResponseError, LLMUpstreamError } from './llmErrors';

/**
 * OpenAI 兼容接口生成参数
 */
export interface OpenAICompatibleGenerateParams {
  /** API 端点地址 */
  endpoint: string;
  /** API 密钥 */
  apiKey: string;
  /** 模型名称 */
  model: string;
  /** 消息列表 */
  messages: LLMMessage[];
  /** 最大生成 Token 数 */
  maxTokens?: number;
  /** 采样温度 */
  temperature?: number | null;
  /** nucleus sampling */
  topP?: number | null;
  /** 仅适用于部分兼容后端的扩展采样参数 */
  topK?: number | null;
  /** 仅适用于部分兼容后端的扩展采样参数 */
  topA?: number | null;
  /** 仅适用于部分兼容后端的扩展采样参数 */
  minP?: number | null;
  /** 仅适用于部分兼容后端的扩展采样参数 */
  repetitionPenalty?: number | null;
  /** 主题重复惩罚 */
  presencePenalty?: number | null;
  /** 词频重复惩罚 */
  frequencyPenalty?: number | null;
  /** 是否使用流式传输 */
  stream?: boolean;
  /** 请求标识 ID */
  requestId: string;
  /** 可选的中断信号 */
  signal?: AbortSignal;
}

export interface OpenAICompatibleEmbeddingParams {
  /** API 端点地址 */
  endpoint: string;
  /** API 密钥 */
  apiKey: string;
  /** 模型名称 */
  model: string;
  /** 需要向量化的文本 */
  input: string;
  /** 可选维度 */
  dimensions?: number;
  /** 请求标识 ID */
  requestId: string;
  /** 可选的中断信号 */
  signal?: AbortSignal;
}

// ============================================
// OpenAI 兼容客户端
// ============================================

/**
 * 处理所有遵循 OpenAI Chat Completions 规范的 API 调用。
 * 包含标准生成、Embedding 与 SSE 流式生成。
 */
export class OpenAICompatibleClient {
  private static readonly TAG = 'OpenAICompatibleClient';

  // ============================================
  // 公开方法 (生成)
  // ============================================

  /**
   * 执行标准非流式生成请求
   * @param params 生成参数
   * @returns 生成的文本内容
   */
  static async generate(params: OpenAICompatibleGenerateParams): Promise<string> {
    const requestBody = {
      model: params.model,
      messages: params.messages,
      max_tokens: params.maxTokens,
      temperature: params.temperature ?? undefined,
      top_p: params.topP ?? undefined,
      top_k: params.topK ?? undefined,
      top_a: params.topA ?? undefined,
      min_p: params.minP ?? undefined,
      repetition_penalty: params.repetitionPenalty ?? undefined,
      presence_penalty: params.presencePenalty ?? undefined,
      frequency_penalty: params.frequencyPenalty ?? undefined,
      stream: params.stream ?? false,
    };

    this.logRequest(params.requestId, params.endpoint, requestBody);
    const requestStartedAt = Date.now();

    const response = await fetch(params.endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${params.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: params.signal,
    });

    this.logResponse(params.requestId, response.status, response.ok, Date.now() - requestStartedAt);

    if (!response.ok) {
      const detail = await this.readErrorDetail(response);
      throw new LLMUpstreamError(`LLM 请求失败(${response.status}): ${detail}`, {
        requestId: params.requestId,
        provider: 'openai-compatible',
        status: response.status,
      });
    }

    const data = (await response.json()) as Record<string, unknown>;
    const outputText = this.extractOutputText(data);

    if (!outputText) {
      throw new LLMEmptyResponseError('LLM 返回为空', { requestId: params.requestId, provider: 'openai-compatible' });
    }

    return outputText;
  }

  /**
   * 调用 OpenAI 兼容 Embeddings 接口
   */
  static async embed(params: OpenAICompatibleEmbeddingParams): Promise<number[]> {
    const requestBody = {
      model: params.model,
      input: params.input,
      dimensions: params.dimensions,
    };

    this.logRequest(params.requestId, params.endpoint, requestBody);
    const requestStartedAt = Date.now();

    const response = await fetch(params.endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${params.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: params.signal,
    });

    this.logResponse(params.requestId, response.status, response.ok, Date.now() - requestStartedAt);

    if (!response.ok) {
      const detail = await this.readErrorDetail(response);
      throw new LLMUpstreamError(`Embedding 请求失败(${response.status}): ${detail}`, {
        requestId: params.requestId,
        provider: 'openai-compatible',
        status: response.status,
      });
    }

    const data = (await response.json()) as Record<string, unknown>;
    const embedding = this.extractEmbedding(data);

    if (!embedding.length) {
      throw new LLMEmptyResponseError('Embedding 返回为空', {
        requestId: params.requestId,
        provider: 'openai-compatible',
      });
    }

    return embedding;
  }

  /**
   * 执行 SSE 流式生成请求
   * @param params 生成参数
   * @param handlers 流式回调处理器
   * @returns 最终拼接的完整文本
   */
  static async generateStream(
    params: OpenAICompatibleGenerateParams,
    handlers: LLMStreamHandlers = {},
  ): Promise<string> {
    const requestBody = {
      model: params.model,
      messages: params.messages,
      max_tokens: params.maxTokens,
      temperature: params.temperature ?? undefined,
      top_p: params.topP ?? undefined,
      top_k: params.topK ?? undefined,
      top_a: params.topA ?? undefined,
      min_p: params.minP ?? undefined,
      repetition_penalty: params.repetitionPenalty ?? undefined,
      presence_penalty: params.presencePenalty ?? undefined,
      frequency_penalty: params.frequencyPenalty ?? undefined,
      stream: true,
    };

    this.logRequest(params.requestId, params.endpoint, requestBody);
    const requestStartedAt = Date.now();

    const response = await fetch(params.endpoint, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${params.apiKey}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
      signal: params.signal,
    });

    this.logResponse(params.requestId, response.status, response.ok, Date.now() - requestStartedAt);

    if (!response.ok) {
      const detail = await this.readErrorDetail(response);
      throw new LLMUpstreamError(`LLM 请求失败(${response.status}): ${detail}`, {
        requestId: params.requestId,
        provider: 'openai-compatible',
        status: response.status,
      });
    }

    if (!response.body) {
      throw new LLMUpstreamError('LLM 流式响应体为空', {
        requestId: params.requestId,
        provider: 'openai-compatible',
      });
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder('utf-8');
    let buffer = '';
    let fullText = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      // 解码并将新块添加到缓冲区
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');

      // 最后一项可能是不完整的行，留到下一次处理
      buffer = lines.pop() ?? '';

      for (const rawLine of lines) {
        const line = rawLine.trim();
        if (!line || !line.startsWith('data:')) continue;

        const payload = line.slice(5).trim();
        if (payload === '[DONE]') continue;

        try {
          const data = JSON.parse(payload) as Record<string, unknown>;
          const deltaText = this.extractStreamDeltaText(data);
          if (!deltaText) continue;

          fullText += deltaText;
          handlers.onChunk?.(deltaText, fullText);
        } catch (error) {
          LogService.warn(this.TAG, `[${params.requestId}] 流式分片解析失败，已跳过该分片`, error);
        }
      }
    }

    if (!fullText.trim()) {
      throw new LLMEmptyResponseError('LLM 返回为空', { requestId: params.requestId, provider: 'openai-compatible' });
    }

    return fullText.trim();
  }

  // ============================================
  // 内部辅助方法 (解析与记录)
  // ============================================

  /**
   * 从标准响应 JSON 中提取文本输出
   */
  private static extractOutputText(data: Record<string, unknown>): string {
    const choices = data.choices;
    if (!Array.isArray(choices) || choices.length === 0) return '';

    const firstChoice = choices[0];
    if (!firstChoice || typeof firstChoice !== 'object') return '';

    const message = (firstChoice as Record<string, unknown>).message;
    if (!message || typeof message !== 'object') return '';

    const content = (message as Record<string, unknown>).content;
    if (typeof content === 'string') {
      return content.trim();
    }

    // 处理部分 API 返回的 content 为 chunks 数组的情况
    if (Array.isArray(content)) {
      const textParts: string[] = [];
      for (const chunk of content) {
        if (!chunk || typeof chunk !== 'object') continue;
        const chunkText = (chunk as Record<string, unknown>).text;
        if (typeof chunkText === 'string' && chunkText.trim().length > 0) {
          textParts.push(chunkText);
        }
      }
      return textParts.join('\n').trim();
    }

    return '';
  }

  /**
   * 从流式分片 JSON 中提取增量文本
   */
  private static extractStreamDeltaText(data: Record<string, unknown>): string {
    const choices = data.choices;
    if (!Array.isArray(choices) || choices.length === 0) return '';

    const firstChoice = choices[0];
    if (!firstChoice || typeof firstChoice !== 'object') return '';

    const delta = (firstChoice as Record<string, unknown>).delta;
    if (!delta || typeof delta !== 'object') return '';

    const content = (delta as Record<string, unknown>).content;
    if (typeof content === 'string') return content;

    // 处理部分 API 返回的 delta content 为数组的情况
    if (Array.isArray(content)) {
      const textParts: string[] = [];
      for (const chunk of content) {
        if (!chunk || typeof chunk !== 'object') continue;
        const text = (chunk as Record<string, unknown>).text;
        if (typeof text === 'string' && text.length > 0) {
          textParts.push(text);
        }
      }
      return textParts.join('');
    }

    return '';
  }

  /**
   * 从 embeddings 响应 JSON 中提取向量
   */
  private static extractEmbedding(data: Record<string, unknown>): number[] {
    const rows = data.data;
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const firstRow = rows[0];
    if (!firstRow || typeof firstRow !== 'object') return [];

    const embedding = (firstRow as Record<string, unknown>).embedding;
    if (!Array.isArray(embedding)) return [];

    return embedding.filter(item => typeof item === 'number' && Number.isFinite(item));
  }

  /**
   * 记录请求详情
   */
  private static logRequest(requestId: string, endpoint: string, requestBody: Record<string, unknown>): void {
    const payload = JSON.stringify(requestBody);
    LogService.info(this.TAG, `[${requestId}] 请求发送: ${endpoint}`, {
      payloadSize: payload.length,
      payloadPreview: this.truncateText(payload, 1200),
    });
  }

  /**
   * 记录响应状态
   */
  private static logResponse(requestId: string, status: number, ok: boolean, requestMs: number): void {
    LogService.info(this.TAG, `[${requestId}] 响应状态: ${status} (${ok ? 'OK' : 'ERROR'})`, {
      requestMs,
    });
  }

  /**
   * 文本截断（用于日志预览）
   */
  private static truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
      return text;
    }
    return `${text.slice(0, maxLength)}...(truncated)`;
  }

  /**
   * 读取并解析错误详情
   */
  private static async readErrorDetail(response: Response): Promise<string> {
    const rawText = await response.text();
    if (!rawText) return 'Empty error body';

    try {
      const json = JSON.parse(rawText) as Record<string, unknown>;
      return JSON.stringify(json);
    } catch {
      return rawText;
    }
  }
}
